// Static content for the coachee help guides ("quick start guide"). Ported
// from the ll-ux-sandpit prototype (coachee-help-guide-page). Presentation-only
// content for now — the program-handbook specifics are illustrative and would
// later be sourced from the cohort's coaching config.

export interface QuickStartGuideBlock {
  body?: string;
  footer?: string;
  icon?: string;
  items?: string[];
  step?: number;
  title: string;
}

export interface QuickStartGuideSection {
  blocks: QuickStartGuideBlock[];
  icon: string;
  id: string;
  intro?: string;
  layout: 'cards' | 'steps';
  title: string;
}

export interface QuickStartGuideSupportContact {
  detail: string;
  email?: string;
  icon: string;
  label: string;
}

export const HOW_COACHING_WORKS: QuickStartGuideSection = {
  id: 'how-coaching-works',
  title: 'How coaching works',
  icon: 'ph:lightbulb',
  layout: 'steps',
  blocks: [
    {
      step: 1,
      title: 'Complete your profile',
      body: 'Tell us about yourself, your goals, and your preferences. This helps us recommend coaches who best match your needs.',
    },
    {
      step: 2,
      title: 'Find your coach',
      body: "Browse coach profiles or explore your recommended matches. Review each coach's experience, coaching style, expertise, languages, and availability to find the right fit.",
    },
    {
      step: 3,
      title: 'Book a chemistry call',
      body: "Meet your coach in a short introductory session to discuss your goals, ask questions, and see if they're the right fit for you.",
    },
    {
      step: 4,
      title: 'Schedule your coaching sessions',
      body: "If it's a good match, book your coaching sessions at times that suit your schedule.",
    },
    {
      step: 5,
      title: 'Meet with your coach',
      body: 'Join your coaching sessions to explore challenges, build new skills, and create practical actions that support your development.',
    },
    {
      step: 6,
      title: 'Take action between sessions',
      body: 'Complete agreed actions, reflect on your progress, and apply your learning in your day-to-day work.',
    },
    {
      step: 7,
      title: 'Continue your journey',
      body: 'Book future sessions, track your progress, revisit previous conversations, and access learning resources to support your ongoing growth.',
    },
  ],
};

export const CHOOSING_A_COACH: QuickStartGuideSection = {
  id: 'choosing-a-coach',
  title: 'Choosing the right coach',
  icon: 'ph:hand-pointing',
  layout: 'cards',
  intro:
    'Finding the right coach makes a real difference to your experience. Use these tips when reviewing profiles and building your shortlist.',
  blocks: [
    {
      icon: 'ph:identification-card',
      title: 'Review the full profile',
      body: "Read the coach's bio, specialties, and background. Flip the coach card to see credentials, industries worked with, and coaching approach. Look for experience that relates to your goals.",
    },
    {
      icon: 'ph:chart-bar',
      title: 'Compare your top matches',
      body: 'Your matches are ranked based on your goals and working preferences. Pay attention to match percentage, timezone, languages, and areas of expertise, not just the headline bio.',
    },
    {
      icon: 'ph:heart',
      title: 'Shortlist before you book',
      body: 'Add coaches to your shortlist as you explore. This lets you compare side by side before committing to a chemistry call. You can shortlist as many coaches as you like.',
    },
    {
      icon: 'ph:phone-call',
      title: 'Book chemistry calls',
      body: 'Use your chemistry call credits to meet shortlisted coaches for a 30-minute intro. There is no obligation to continue with a coach after the call. The goal is to find the right fit.',
    },
    {
      icon: 'ph:sparkle',
      title: 'Trust your instinct',
      body: "Chemistry matters. After a call, ask yourself whether you felt heard, challenged appropriately, and confident in the coach's style. It is fine to meet more than one coach before deciding.",
    },
  ],
};

export const COACHING_HANDBOOK: QuickStartGuideSection = {
  id: 'coaching-handbook',
  title: 'Program handbook',
  icon: 'ph:book-open',
  layout: 'cards',
  intro: 'Key details about your coaching program and session credits.',
  blocks: [
    {
      title: 'Program overview',
      items: [
        'Coaching sessions: one-to-one sessions booked from your credit balance.',
        'Chemistry calls: short intro calls (separate from your session credits).',
      ],
      footer:
        'Your program combines executive coaching, peer learning, and curated resources to support your development.',
    },
    {
      title: 'Session credits',
      items: [
        'Coaching sessions are booked from your session credit balance.',
        'Chemistry calls use separate credits and do not reduce your session total.',
        'Credits must be used within the program period unless your organization agrees an extension.',
        "Sessions cancelled or missed outside your program's cancellation window may be deducted from your balance.",
      ],
    },
    {
      title: 'Scheduling rules',
      items: [
        'Book sessions ahead of time where possible so your coach can prepare.',
        "You can reschedule up until your program's cancellation window before the session.",
        'Sessions are held by video call. Join from the calendar invite when it is time.',
        'Your coach may share resources or follow-up notes after each session.',
      ],
    },
  ],
};

// All guide sections in reading order, for the single-page accordion.
export const QUICK_START_GUIDE_SECTIONS: QuickStartGuideSection[] = [
  HOW_COACHING_WORKS,
  CHOOSING_A_COACH,
  COACHING_HANDBOOK,
];

export const QUICK_START_GUIDE_SUPPORT_ITEMS: QuickStartGuideSupportContact[] =
  [
    {
      icon: 'ph:envelope-simple',
      label: 'Program administrator',
      detail: 'Questions about your cohort, credits, or program rules.',
      email: 'v-support@hultashridge.com',
    },
    {
      icon: 'ph:warning-circle',
      label: 'Urgent session issues',
      detail:
        'Message your coach directly or contact support before the session starts.',
    },
  ];
